import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import AsyncSelect from 'react-select/async'
import { toast } from 'react-toastify'
import { getTheme } from '../../../redux/user/userSlice'
import { putFusion } from '../../../apiHelper/fusionApi'
import { FUSION_URL } from '../../../apiHelper/helper'
import Button from '../../../components/common/Button/Button'
import { insuranceSearch } from './insuranceHelper'
import { GRAY_300, GRAY_400, GRAY_800, GRAY_950, WHITE } from '../../../scss/colorConsts'
import './InsuranceCaseInformation.scss'

const selectStyleDark = {
  control: (styles) => ({
    ...styles,
    backgroundColor: 'var(--ion-background-color)',
    color: 'var(--reactselect-input-color)',
    borderColor: GRAY_400,
    boxShadow: 'none',
  }),
  input: (styles) => ({
    ...styles,
    color: 'var(--reactselect-input-color)',
  }),
  menu: (styles) => ({
    ...styles,
    backgroundColor: 'var(--reactselect-input-bg-color)',
    color: 'var(--reactselect-input-color)',
  }),
  option: (styles, { data, isFocused, isSelected }) => {
    return {
      ...styles,
      backgroundColor: isSelected
        ? 'var(--text-glow-color)'
        : isFocused
          ? 'var(--text-glow-color)'
          : GRAY_800,
      color: isSelected ? GRAY_950 : isFocused ? GRAY_950 : data?.color,
      cursor: 'pointer',
      borderBottom: `1px solid ${GRAY_300}`,
      'last-child': {
        border: 'none',
      },
      ':active': {
        ...styles[':active'],
        backgroundColor: 'var(--text-glow-color)',
      },
    }
  },
  singleValue: (styles, { data }) => ({ ...styles, color: 'var(--reactselect-input-color)' }),
}

const selectStyleLight = {
  control: (styles) => ({
    ...styles,
    backgroundColor: 'var(--reactselect-input-bg-color)',
    color: 'var(--reactselect-input-color)',
    boxShadow: 'none',
  }),
  input: (styles) => ({ ...styles, color: 'var(--reactselect-input-color)' }),
  menu: (styles) => ({ ...styles, backgroundColor: 'var(--reactselect-input-bg-color)' }),
  option: (styles, { data, isDisabled, isFocused, isSelected }) => {
    return {
      ...styles,
      backgroundColor: isSelected
        ? 'var(--text-glow-color)'
        : isFocused
          ? 'var(--text-glow-color)'
          : WHITE,
      color: GRAY_950,
      cursor: 'pointer',
      borderBottom: `1px solid ${GRAY_300}`,
      'last-child': {
        border: 'none',
      },
      ':active': {
        ...styles[':active'],
        backgroundColor: 'var(--text-glow-color)',
      },
    }
  },
  singleValue: (styles, { data }) => ({ ...styles, color: 'var(--reactselect-input-color)' }),
}

async function activeInsuranceSet({ insuranceData, patient_id }) {
  try {
    if (!patient_id) {
      toast.error('Error sending patient info')
      return {}
    }
    if (insuranceData) {
      const insuranceSearchEndpoint = FUSION_URL + 'v1/patient/insurance/' + patient_id
      return await putFusion(insuranceSearchEndpoint, insuranceData)
    }
    return {}
  } catch (err) {
    console.error('Error setting insurances', err)
    return {}
  }
}

function sortInsurance(insurances) {
  return insurances.toSorted((a, b) => a?.label.localeCompare(b?.label))
}

const fetchInsurances = async (searchString) => {
  let ins = await insuranceSearch({ searchString })
  let namedSearch = []
  let aliasSearch = []

  ins.forEach((ins) => {
    let mappedIns = {
      label: ins.name + (ins.primaryPayerId ? ` (${ins.primaryPayerId})` : ''),
      value: ins.name,
      stediID: ins.primaryPayerId,
    }

    if (ins?.name?.toLowerCase()?.includes(searchString?.toLowerCase())) {
      namedSearch.push(mappedIns)
    } else {
      aliasSearch.push(mappedIns)
    }
  })

  return [...sortInsurance(namedSearch), ...sortInsurance(aliasSearch)]
}

// const debouncedInsuranceSearch = _.debounce(async (searchString, onSuccess) => {
//   onSuccess(await insuranceSearch({ searchString }))
// }, 300)

const PLAN_TYPES = [
  '',
  'Auto Insurance Policy',
  'Commercial',
  'Consolidated Omnibus Budget Reconciliation Act (COBRA)',
  'Medicare Conditionally Primary',
  'Disability',
  'Disability Benefits',
  'Dual',
  'Exclusive Provider Organization',
  'Family or Friends',
  'Group Policy',
  'Health Maintenance Organization (HMO)',
  'Health Maintenance Organization (HMO) - Medicare Risk',
  'Special Low Income Medicare Beneficiary',
  'Indemnity',
  'Individual Policy',
  'Long Term Care',
  'Long Term Policy',
  'Life Insurance',
  'Litigation',
  'Medicare Part A',
  'Medicare Part B',
  'Medicaid',
  'Medicaid Advantage',
  'Medigap Part A',
  'Medigap Part B',
  'Medicare Primary',
  'Medicare Advantage',
  'Military/Veterans',
  'Other',
  'Property Insurance - Personal',
  'Personal',
  'Personal Payment (Cash - No Insurance)',
  'Preferred Provider Organization (PPO)',
  'Point of Service (POS)',
  'Qualified Medicare Beneficiary',
  'Property Insurance - Real',
  'Supplemental Policy',
  'Tax Equity Fiscal Responsibility Act (TEFRA)',
  'Workers Compensation',
  'Wrap Up Policy',
]

export default function InsuranceCaseInformation({ patient, onRefreshPatient }) {
  const activeInsurance = patient?.active_insurance || {}

  const theme = useSelector(getTheme)

  const [submitCalled, setSubmitCalled] = useState(false)
  const [selectedPayer, setSelectedPayer] = useState({
    label: activeInsurance?.name,
    value: activeInsurance?.name,
    stediID: activeInsurance?.stediID,
  })
  const [selectedIdPolicy, setSelectedIdPolicy] = useState(activeInsurance?.member_id)
  const [selectedPlan, setSelectedPlan] = useState(activeInsurance?.plan_type)
  const [isEditable, setIsEditable] = useState(false)

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'end' }}>
        <div onClick={() => setIsEditable(!isEditable)} style={{ cursor: 'pointer' }}>
          {!isEditable ? (
            <i className="fa-solid fa-pen" style={{ color: GRAY_400 }}></i>
          ) : (
            <div
              onClick={() => {
                setSelectedPayer({
                  label: null,
                  value: null,
                })
                setSelectedIdPolicy(null)
                setSelectedPlan(null)
              }}
              style={{ color: 'var(--default-text-color)' }}
            >
              Clear
            </div>
          )}
        </div>
      </div>
      <div className={'activeInsurance'}>
        <div className={'activeInsuranceWrapper'} style={{ width: '50%' }}>
          <label>Insurance Name/Payer</label>
          {isEditable ? (
            <AsyncSelect
              isSearchable
              loadOptions={fetchInsurances}
              noOptionsMessage={() => 'No results found'}
              onChange={(e) => {
                setSelectedPayer(e)
              }}
              styles={theme === 'light' ? selectStyleLight : selectStyleDark}
              value={selectedPayer}
            />
          ) : (
            (selectedPayer?.value || '') +
              (selectedPayer.stediID ? ` (${selectedPayer.stediID})` : '') || '-'
          )}
        </div>
        <div className={'activeInsuranceWrapper'}>
          <label>Insurance Plan Type</label>
          {isEditable ? (
            <select onChange={(e) => setSelectedPlan(e.target.value)} value={selectedPlan}>
              {PLAN_TYPES.map((plan) => {
                return (
                  <option key={plan} value={plan}>
                    {plan}
                  </option>
                )
              })}
            </select>
          ) : (
            selectedPlan || '-'
          )}
        </div>
        <div className={'activeInsuranceWrapper'}>
          <label>Member ID / Policy Number</label>
          {isEditable ? (
            <input
              onChange={(e) => setSelectedIdPolicy(e.currentTarget.value)}
              value={selectedIdPolicy}
              maxLength={50}
            />
          ) : (
            selectedIdPolicy || '-'
          )}
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'end',
          }}
        >
          {isEditable && (
            <Button
              disabled={submitCalled}
              onClick={async () => {
                setSubmitCalled(true)
                setIsEditable(false)

                await activeInsuranceSet({
                  patient_id: patient?.patient_id,
                  insuranceData: {
                    ...activeInsurance,
                    name: selectedPayer?.value,
                    plan_type: selectedPlan,
                    member_id: selectedIdPolicy,
                    stediID: selectedPayer?.stediID || selectedPayer?.label,
                  },
                })

                setSubmitCalled(false)
                onRefreshPatient()
              }}
              compressed
            >
              Save
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}
